"use client"

import { Heading, Text } from "@chakra-ui/react"
import { Badge } from "@chakra-ui/react/badge"
import { Flex } from "@chakra-ui/react/flex"
import { HStack, VStack } from "@chakra-ui/react/stack"
import parse from "html-react-parser"

import { Education } from "@/types/getAbout"
import { getSafeHtmlParserOptions } from "@/utils/getSafeHtmlParserOptions"

import { EducationDuration } from "./utils"

interface AccordionEducationHeaderProps {
  education: Education
  duration: EducationDuration
}

const AccordionEducationHeader = ({
  duration,
  education,
}: AccordionEducationHeaderProps) => {
  const { durationMonths, durationYears, endDate, startDate } = duration

  return (
    <Flex flexGrow={1} justifyContent="space-between">
      <HStack alignItems="flex-start">
        <Flex
          _dark={{
            borderColor: "gray.700",
          }}
          alignContent="center"
          alignItems="center"
          bg="white"
          borderColor="gray.100"
          borderRadius={"lg"}
          borderWidth="1px"
          css={{
            "& svg": {
              height: "40px",
              width: "40px",
            },
          }}
          flexDirection="column"
          height="54px"
          justifyContent="center"
          maxH="54px"
          minW="54px"
          padding={1}
          width="54px"
        >
          {parse(education.company.svg_logo, getSafeHtmlParserOptions())}
        </Flex>
        <VStack
          alignItems={"flex-start"}
          gap={0}
          justifyContent={"flex-start"}
          pl={3}
        >
          <Text color="gray.400" fontSize={["xs", "sm", "sm"]} textAlign="left">
            {education.course_name}
          </Text>

          <Heading
            fontSize={["sm", "md", "lg"]}
            lineHeight={{ base: "1.2", md: "1.5" }}
            textAlign="left"
          >
            {education.company.name}
          </Heading>
        </VStack>
      </HStack>

      <VStack alignItems={"flex-end"} gap="0px" justifyContent="center">
        <Text
          color={"fg.muted"}
          css={{
            textWrap: "nowrap",
          }}
          fontSize="xs"
          textAlign="right"
        >
          <Text as="span" display={"inline-flex"} textAlign={"right"}>
            {startDate.format("MMM YYYY")}
          </Text>
          {" - "}
          <Text as="span" display={"inline-flex"} textAlign={"right"}>
            {education.end_date == null ? (
              <Badge
                _dark={{
                  bgColor: "primary.800",
                  color: "primary.50",
                }}
                bgColor="primary.50"
                color="primary.800"
                colorPalette="brand"
              >
                Present
              </Badge>
            ) : (
              endDate.format("MMM YYYY")
            )}
          </Text>
        </Text>
        <Text color={"fg.subtle"} fontSize="xs">
          {0 < durationYears && (
            <Text as="span">
              {durationYears}
              {1 < durationYears ? " Years" : " Year"}
            </Text>
          )}

          {durationMonths !== 0 && (
            <Text as="span">
              {0 < durationYears && <Text as="span">, </Text>}
              {durationMonths}
              {1 < durationMonths ? " Months" : " Month"}
            </Text>
          )}
        </Text>
      </VStack>
    </Flex>
  )
}

export default AccordionEducationHeader
